import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Menu,
  ChevronRight,
  ArrowLeft,
  KeyRound,
  Phone,
  Truck,
  LogOut,
  Loader2,
  Settings,
} from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useAppSelector, useAppDispatch } from '../../store';
import { setUser } from '../../store/slices/authSlice';
import { useMdtNavigation } from '../../contexts/MdtNavigationContext';
import MdtLogoutConfirmModal from './MdtLogoutConfirmModal';

type MenuView = 'root' | 'change-password' | 'update-phone';

interface MdtMenuProps {
  onLogout: () => Promise<void>;
  loggingOut?: boolean;
}

export default function MdtMenu({ onLogout, loggingOut = false }: MdtMenuProps) {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector((state) => state.auth);
  const { changeUnit } = useMdtNavigation();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>('root');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [changingUnit, setChangingUnit] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSuccess, setPhoneSuccess] = useState(false);

  const closeMenu = () => {
    setOpen(false);
    setView('root');
    setPasswordError(null);
    setPasswordSuccess(false);
    setPhoneError(null);
    setPhoneSuccess(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPhoneNumber(user?.phoneNumber || '');
  };

  const handleChangeUnit = async () => {
    setChangingUnit(true);
    try {
      await changeUnit();
      closeMenu();
    } finally {
      setChangingUnit(false);
    }
  };

  const handlePasswordSave = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);
    if (!currentPassword.trim()) {
      setPasswordError('Enter your current password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPasswordError(data.error || 'Failed to update password');
        return;
      }
      if (data.user) dispatch(setUser(data.user));
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setPasswordError('Network error — try again');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handlePhoneSave = async () => {
    setPhoneError(null);
    setPhoneSuccess(false);
    setPhoneSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber: phoneNumber.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhoneError(data.error || 'Failed to update phone number');
        return;
      }
      if (data.user) dispatch(setUser(data.user));
      setPhoneSuccess(true);
    } catch {
      setPhoneError('Network error — try again');
    } finally {
      setPhoneSaving(false);
    }
  };

  const handleLogoutConfirm = async () => {
    await onLogout();
    setShowLogoutConfirm(false);
    closeMenu();
  };

  const panelTitle =
    view === 'change-password' ? 'Change password'
      : view === 'update-phone' ? 'Update phone number'
        : 'Menu';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-7 h-7 rounded-md flex items-center justify-center transition-colors"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
        title="Menu"
      >
        <Menu className="w-4 h-4 text-white/80" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[500] flex justify-end"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={closeMenu}
        >
          <div
            className="w-full max-w-xs h-full flex flex-col shadow-2xl"
            style={{ background: '#141820', borderLeft: '1px solid rgba(255,255,255,0.08)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                {view !== 'root' && (
                  <button
                    type="button"
                    onClick={() => setView('root')}
                    className="p-1 rounded-lg hover:bg-white/[0.06]"
                  >
                    <ArrowLeft className="w-4 h-4 text-white/60" />
                  </button>
                )}
                <span className="text-sm font-bold text-white">{panelTitle}</span>
              </div>
              <button type="button" onClick={closeMenu} className="p-1 rounded-lg hover:bg-white/[0.06]">
                <X className="w-4 h-4 text-white/50" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {view === 'root' && (
                <div className="space-y-1">
                  <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/30 flex items-center gap-1.5">
                    <Settings className="w-3 h-3" />
                    Settings
                  </p>
                  <button
                    type="button"
                    onClick={() => setView('change-password')}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-white/[0.04] transition-colors"
                  >
                    <KeyRound className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-white/90">Change password</span>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPhoneNumber(user?.phoneNumber || '');
                      setView('update-phone');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-white/[0.04] transition-colors"
                  >
                    <Phone className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-white/90">Update phone number</span>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </button>

                  <p className="px-2 py-1.5 mt-2 text-[10px] font-semibold uppercase tracking-wider text-white/30 flex items-center gap-1.5">
                    <Truck className="w-3 h-3" />
                    Change unit
                  </p>
                  <button
                    type="button"
                    onClick={handleChangeUnit}
                    disabled={changingUnit}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                  >
                    <Truck className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="flex-1 text-sm text-white/90">Select different unit</span>
                    {changingUnit ? (
                      <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-white/30" />
                    )}
                  </button>

                  <div className="my-3 border-t border-white/[0.08]" />

                  <button
                    type="button"
                    onClick={() => setShowLogoutConfirm(true)}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <span className="flex-1 text-sm font-semibold text-red-400">Logout</span>
                  </button>
                </div>
              )}

              {view === 'change-password' && (
                <div className="space-y-3 px-1">
                  <p className="text-xs text-white/45">Enter your current password, then choose a new one.</p>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-white/40 block mb-1.5">Current password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white bg-white/[0.05] border border-white/[0.08] outline-none focus:border-primary/40"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-white/40 block mb-1.5">New password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white bg-white/[0.05] border border-white/[0.08] outline-none focus:border-primary/40"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-white/40 block mb-1.5">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white bg-white/[0.05] border border-white/[0.08] outline-none focus:border-primary/40"
                    />
                  </div>
                  {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
                  {passwordSuccess && <p className="text-xs text-emerald-400">Password updated successfully.</p>}
                  <button
                    type="button"
                    disabled={passwordSaving}
                    onClick={handlePasswordSave}
                    className={cn(
                      'w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2',
                      'bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 disabled:opacity-50',
                    )}
                  >
                    {passwordSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save password
                  </button>
                </div>
              )}

              {view === 'update-phone' && (
                <div className="space-y-3 px-1">
                  <p className="text-xs text-white/45">This number may be used for operational contact.</p>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-white/40 block mb-1.5">Phone number</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      autoComplete="tel"
                      placeholder="e.g. 07xxx xxxxxx"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white bg-white/[0.05] border border-white/[0.08] outline-none focus:border-primary/40"
                    />
                  </div>
                  {phoneError && <p className="text-xs text-red-400">{phoneError}</p>}
                  {phoneSuccess && <p className="text-xs text-emerald-400">Phone number saved.</p>}
                  <button
                    type="button"
                    disabled={phoneSaving}
                    onClick={handlePhoneSave}
                    className={cn(
                      'w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2',
                      'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50',
                    )}
                  >
                    {phoneSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save phone number
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      <MdtLogoutConfirmModal
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogoutConfirm}
        loggingOut={loggingOut}
      />
    </>
  );
}
